(() => {
  const money = (cents, format) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, format);
    }
    const value = (Number(cents || 0) / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (!format) return `$${value}`;
    return format.replace(/\{\{\s*(amount|amount_with_comma_separator|amount_no_decimals|amount_no_decimals_with_comma_separator)\s*\}\}/, value);
  };

  class PeakSimBuilder {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-builder-config]').textContent);
      this.categories = [...root.querySelectorAll('[data-category]')];
      this.merchantCategories = [...this.categories];
      this.rowTemplates = new Map(this.categories.map(category => [category.dataset.categoryId, category.cloneNode(true)]));
      this.extraRows = [];
      this.state = {};
      this.productVisibility = new Map();
      this.showOutOfStock = false;
      this.restore();
      this.sortProductsByAvailability();
      this.bind();
      this.initProductVisibility();
      this.refreshCards();
      this.render();
      this.initMobileReviewVisibility();
    }

    bindCategory(category) {
      category.querySelector('[data-open-category]').addEventListener('click', () => this.toggle(category));
      category.querySelector('[data-clear-category]').addEventListener('click', () => this.clear(category.dataset.categoryId));

      category.querySelectorAll('[data-product-card]').forEach(card => {
        card.querySelectorAll('[data-product-option]').forEach(select => {
          select.addEventListener('change', () => this.refreshCard(card, select));
        });

        card.querySelector('[data-select-product]').addEventListener('click', () => this.select(category, card));
      });

      const showMore = category.querySelector('[data-show-more]');
      if (showMore) {
        showMore.addEventListener('click', () => {
          if (showMore.dataset.action === 'close') {
            this.closeCategoryAndReturn(category);
          } else {
            this.track('peak_builder_show_more', { category_id: category.dataset.categoryId });
            this.showMoreProducts(category);
          }
        });
      }

      category.querySelectorAll('[data-view-product]').forEach(link => {
        link.addEventListener('click', () => {
          const card = link.closest('[data-product-card]');
          const category = link.closest('[data-category]');
          this.track('peak_builder_product_view', {
            product_id: card ? card.dataset.productId : '',
            category_id: category ? category.dataset.categoryId : ''
          });
        });
      });
    }

    bind() {
      this.categories.forEach(category => this.bindCategory(category));
      const add = this.root.querySelector('[data-add-another]');
      const picker = this.root.querySelector('[data-extra-category-picker]');
      const select = this.root.querySelector('[data-extra-category-select]');
      if (add && picker && select) {
        add.disabled = this.merchantCategories.length === 0;
        add.addEventListener('click', () => {
          picker.hidden = !picker.hidden;
          add.setAttribute('aria-expanded', String(!picker.hidden));
          if (!picker.hidden) select.focus();
        });
        this.root.querySelector('[data-confirm-extra-row]').addEventListener('click', () => {
          const category = this.addExtraRow(select.value);
          if (!category) return;
          picker.hidden = true;
          add.setAttribute('aria-expanded', 'false');
          this.openCategory(category);
          category.querySelector('[data-open-category]').focus();
        });
      }

      const mobileReview = this.root.querySelector('[data-mobile-review]');
      if (mobileReview) {
        mobileReview.addEventListener('click', () => {
          this.track('peak_builder_review_build', { item_count: Object.keys(this.state).length });
          this.scrollToElement(this.root.querySelector('[data-builder-summary]'));
        });
      }

      const stockToggle = this.root.querySelector('[data-show-out-of-stock]');
      if (stockToggle) {
        try {
          this.showOutOfStock = sessionStorage.getItem(`${this.config.storageKey}-show-oos`) === 'true';
        } catch (error) {}
        stockToggle.checked = this.showOutOfStock;
        stockToggle.addEventListener('change', () => {
          this.showOutOfStock = stockToggle.checked;
          try {
            sessionStorage.setItem(`${this.config.storageKey}-show-oos`, String(this.showOutOfStock));
          } catch (error) {}
          this.track('peak_builder_out_of_stock_toggle', { enabled: this.showOutOfStock });
          this.categories.forEach(category => this.updateProductVisibility(category));
        });
      }

      this.root.querySelectorAll('[data-reset-build]').forEach(button => {
        button.addEventListener('click', () => this.clearBuild());
      });

      this.root.querySelector('[data-add-build]').addEventListener('click', () => this.addToCart());
    }

    createExtraRow(sourceId, id) {
      const template = this.rowTemplates.get(sourceId);
      if (!template || this.getCategoryById(id)) return null;
      const category = template.cloneNode(true);
      category.dataset.categoryId = id;
      category.dataset.required = 'false';
      category.dataset.sourceCategoryLabel = template.querySelector('.peak-builder__category-label').textContent.trim();
      category.removeAttribute('data-shopify-editor-block');
      // Preserve label associations without duplicating document IDs.
      category.querySelectorAll('[id], [for]').forEach(element => {
        for (const attribute of ['id', 'for']) {
          if (element.hasAttribute(attribute)) {
            element.setAttribute(attribute, `${id}-${element.getAttribute(attribute)}`);
          }
        }
      });
      this.root.querySelector('[data-extra-row-controls]').before(category);
      this.categories.push(category);
      this.extraRows.push({ id, sourceId });
      this.renumberRows();
      return category;
    }

    addExtraRow(sourceId) {
      let id;
      do {
        id = `extra-${crypto.randomUUID()}`;
      } while (this.getCategoryById(id));
      const category = this.createExtraRow(sourceId, id);
      if (!category) return null;
      this.bindCategory(category);
      this.sortProductsByAvailability();
      this.productVisibility.set(id, this.getBatchSize());
      category.querySelectorAll('[data-product-card]').forEach(card => this.refreshCard(card));
      this.persist();
      this.render();
      return category;
    }

    removeExtraRow(id) {
      if (!this.extraRows.some(row => row.id === id)) return;
      const category = this.getCategoryById(id);
      const picker = category.querySelector('[data-picker]');
      if (picker._peakAnimation) picker._peakAnimation.cancel();
      category.remove();
      this.categories = this.categories.filter(row => row !== category);
      this.extraRows = this.extraRows.filter(row => row.id !== id);
      delete this.state[id];
      this.productVisibility.delete(id);
      this.renumberRows();
      this.persist();
      this.render();
      this.root.querySelector('[data-add-another]').focus();
    }

    renumberRows() {
      this.categories.forEach((category, index) => {
        category.querySelector('.peak-builder__number').textContent = String(index + 1).padStart(2, '0');
      });
      this.extraRows.forEach(({ id }, index) => {
        const category = this.getCategoryById(id);
        const label = `Additional Item ${index + 1}`;
        category.querySelector('.peak-builder__category-label').textContent = label;
      });
    }

    toggle(category) {
      const picker = category.querySelector('[data-picker]');
      if (!picker) return;

      if (picker.hidden) {
        this.openCategory(category);
      } else {
        this.closeCategory(category, {
          returnToHeader: false,
          restoreFocus: false,
          reason: 'toggle'
        });
      }
    }

    openCategory(category) {
      const picker = category.querySelector('[data-picker]');
      const button = category.querySelector('[data-open-category]');
      if (!picker || !button || !picker.hidden) return;

      this.track('peak_builder_category_toggle', {
        category_id: category.dataset.categoryId,
        opened: true
      });

      if (picker._peakAnimation) picker._peakAnimation.cancel();

      picker.hidden = false;
      category.classList.add('is-open');
      button.setAttribute('aria-expanded', 'true');

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        typeof picker.animate !== 'function'
      ) return;

      picker._peakAnimation = picker.animate(
        [
          { height: '0px', opacity: 0, transform: 'translateY(-6px)' },
          { height: `${picker.scrollHeight}px`, opacity: 1, transform: 'translateY(0)' }
        ],
        { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );

      picker._peakAnimation.onfinish = () => {
        picker.style.height = '';
        picker.style.opacity = '';
        picker.style.transform = '';
        picker._peakAnimation = null;
      };
    }

    closeCategory(category, {
      returnToHeader = false,
      restoreFocus = false,
      reason = 'toggle'
    } = {}) {
      const picker = category.querySelector('[data-picker]');
      const button = category.querySelector('[data-open-category]');
      if (!picker || !button || picker.hidden) return;

      if (reason === 'toggle') {
        this.track('peak_builder_category_toggle', {
          category_id: category.dataset.categoryId,
          opened: false
        });
      } else if (reason === 'bottom') {
        this.track('peak_builder_category_bottom_close', {
          category_id: category.dataset.categoryId
        });
      }

      if (picker._peakAnimation) picker._peakAnimation.cancel();

      category.classList.remove('is-open');
      button.setAttribute('aria-expanded', 'false');

      const finish = () => {
        picker.hidden = true;
        picker.style.height = '';
        picker.style.opacity = '';
        picker.style.transform = '';
        picker._peakAnimation = null;

        requestAnimationFrame(() => {
          if (restoreFocus) {
            try {
              button.focus({ preventScroll: true });
            } catch (error) {
              button.focus();
            }
          }

          if (returnToHeader) {
            this.scrollToElement(category);
          }
        });
      };

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        typeof picker.animate !== 'function'
      ) {
        finish();
        return;
      }

      picker._peakAnimation = picker.animate(
        [
          { height: `${picker.scrollHeight}px`, opacity: 1, transform: 'translateY(0)' },
          { height: '0px', opacity: 0, transform: 'translateY(-6px)' }
        ],
        { duration: 220, easing: 'cubic-bezier(0.4, 0, 1, 1)' }
      );

      picker._peakAnimation.onfinish = finish;
    }

    clearBuild() {
      this.state = {};
      this.persist();

      this.categories.forEach(category => {
        const picker = category.querySelector('[data-picker]');
        const button = category.querySelector('[data-open-category]');
        if (picker._peakAnimation) picker._peakAnimation.cancel();
        picker.hidden = true;
        category.classList.remove('is-open');
        button.setAttribute('aria-expanded', 'false');
      });

      this.render();
      const status = this.root.querySelector('[data-builder-status]');
      if (status) status.textContent = 'BUILD CLEARED';
    }

    sortProductsByAvailability() {
      this.categories.forEach(category => {
        const container = category.querySelector('[data-products]');
        if (!container) return;

        const cards = [...container.querySelectorAll('[data-product-card]')];
        const sorted = cards
          .map((card, index) => ({
            card,
            index,
            available: card.dataset.productAvailable === 'true'
          }))
          .sort((a, b) => {
            if (a.available !== b.available) return a.available ? -1 : 1;
            return a.index - b.index;
          });

        sorted.forEach(({ card }) => container.appendChild(card));
      });
    }

    getCategoryIncompatibleIds(category) {
      return new Set(
        String(category.dataset.incompatibleWith || '')
          .split(',')
          .map(value => value.trim())
          .filter(Boolean)
      );
    }

    getCategoryById(id) {
      return this.categories.find(category => category.dataset.categoryId === id) || null;
    }

    getCompatibilityConflict(category, productId) {
      const candidateId = String(productId);
      const candidateRules = this.getCategoryIncompatibleIds(category);

      for (const [selectedCategoryId, item] of Object.entries(this.state)) {
        if (!item || selectedCategoryId === category.dataset.categoryId) continue;

        const selectedProductId = String(item.productId);
        const selectedCategory = this.getCategoryById(selectedCategoryId);
        const selectedRules = selectedCategory
          ? this.getCategoryIncompatibleIds(selectedCategory)
          : new Set();

        if (candidateRules.has(selectedProductId) || selectedRules.has(candidateId)) {
          return item;
        }
      }

      return null;
    }

    getSelectedCompatibilityConflicts() {
      const conflicts = [];
      const entries = Object.entries(this.state);

      for (let i = 0; i < entries.length; i += 1) {
        const [categoryAId, itemA] = entries[i];
        const categoryA = this.getCategoryById(categoryAId);
        if (!categoryA || !itemA) continue;

        const rulesA = this.getCategoryIncompatibleIds(categoryA);

        for (let j = i + 1; j < entries.length; j += 1) {
          const [categoryBId, itemB] = entries[j];
          const categoryB = this.getCategoryById(categoryBId);
          if (!categoryB || !itemB) continue;

          const rulesB = this.getCategoryIncompatibleIds(categoryB);
          const conflictsMatch =
            rulesA.has(String(itemB.productId)) ||
            rulesB.has(String(itemA.productId));

          if (conflictsMatch) {
            conflicts.push({
              a: itemA,
              b: itemB
            });
          }
        }
      }

      return conflicts;
    }

    updateCompatibilityStates() {
      this.categories.forEach(category => {
        category.querySelectorAll('[data-product-card]').forEach(card => {
          const conflict = this.getCompatibilityConflict(category, card.dataset.productId);
          const note = card.querySelector('[data-compatibility-note]');
          const incompatible = Boolean(conflict);

          card.classList.toggle('is-incompatible', incompatible);

          if (note) {
            note.hidden = !incompatible;
            note.textContent = incompatible
              ? `NOT COMPATIBLE WITH ${conflict.title}`
              : '';
          }
        });
      });
    }

    getHeaderOffset() {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      return (parseFloat(raw) || 86) + 16;
    }

    scrollToElement(element) {
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.scrollY - this.getHeaderOffset();
      window.scrollTo({
        top: Math.max(0, top),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    closeCategoryAndReturn(category, reason = 'bottom') {
      this.closeCategory(category, {
        returnToHeader: true,
        restoreFocus: true,
        reason
      });
    }

    track(eventName, detail = {}) {
      const payload = {
        event: eventName,
        builder_section: this.root.dataset.sectionId || '',
        ...detail
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);

      this.root.dispatchEvent(new CustomEvent(eventName, {
        bubbles: true,
        detail: payload
      }));
    }

    initMobileReviewVisibility() {
      const review = this.root.querySelector('[data-mobile-review]');
      const summary = this.root.querySelector('[data-builder-summary]');
      if (!review || !summary || typeof IntersectionObserver !== 'function') return;

      this._summaryVisible = false;
      this._summaryObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        this._summaryVisible = Boolean(entry && entry.isIntersecting);
        this.updateMobileReviewVisibility();
      }, {
        threshold: 0.12
      });

      this._summaryObserver.observe(summary);

      // Exclude the section's trailing padding: the control belongs to builder content.
      this._builderVisible = false;
      this._builderObserver = new IntersectionObserver(entries => {
        this._builderVisible = entries.some(entry => entry.isIntersecting);
        this.updateMobileReviewVisibility();
      }, { threshold: 0 });
      this._builderObserver.observe(this.root.querySelector('.peak-builder__shell') || this.root);
      this.updateMobileReviewVisibility();
    }

    updateMobileReviewVisibility() {
      const review = this.root.querySelector('[data-mobile-review]');
      if (!review) return;

      const selected = Object.values(this.state);
      const hasItems = selected.length > 0;
      const outsideMobileBuilder = window.matchMedia('(max-width: 749px)').matches && this._builderVisible === false;
      review.hidden = !hasItems || Boolean(this._summaryVisible) || outsideMobileBuilder;
    }

    getBatchSize() {
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const value = mobile
        ? Number(this.config.mobileBatch || 4)
        : Number(this.config.desktopBatch || 6);
      return Math.max(1, value);
    }

    initProductVisibility() {
      const batch = this.getBatchSize();
      this.categories.forEach(category => {
        this.productVisibility.set(category.dataset.categoryId, batch);
        this.updateProductVisibility(category);
      });
    }

    showMoreProducts(category) {
      const id = category.dataset.categoryId;
      const current = this.productVisibility.get(id) || this.getBatchSize();
      this.productVisibility.set(id, current + this.getBatchSize());
      this.updateProductVisibility(category);
    }

    updateProductVisibility(category) {
      const cards = [...category.querySelectorAll('[data-product-card]')];
      const button = category.querySelector('[data-show-more]');
      const empty = category.querySelector('[data-filter-empty]');
      if (!button) return;

      const eligibleCards = cards.filter(card =>
        this.showOutOfStock || card.dataset.productAvailable === 'true'
      );

      const id = category.dataset.categoryId;
      const visibleCount = this.productVisibility.get(id) || this.getBatchSize();

      cards.forEach(card => { card.hidden = true; });
      eligibleCards.forEach((card, index) => { card.hidden = index >= visibleCount; });

      if (empty) {
        empty.hidden = eligibleCards.length !== 0;
        empty.innerHTML = this.showOutOfStock
          ? 'No products are currently available in this category.'
          : 'No in-stock products are currently available in this category. <button type="button" class="peak-builder__inline-stock" data-enable-oos>SHOW OUT-OF-STOCK PRODUCTS</button>';

        const enable = empty.querySelector('[data-enable-oos]');
        if (enable) {
          enable.addEventListener('click', () => {
            const toggle = this.root.querySelector('[data-show-out-of-stock]');
            if (toggle) {
              toggle.checked = true;
              toggle.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }, { once: true });
        }
      }

      if (!eligibleCards.length) {
        button.hidden = false;
        button.classList.add('is-close');
        button.dataset.action = 'close';
        button.textContent = 'CLOSE';
        return;
      }

      const remaining = Math.max(0, eligibleCards.length - visibleCount);
      if (remaining === 0) {
        button.hidden = false;
        button.classList.add('is-close');
        button.dataset.action = 'close';
        button.textContent = 'CLOSE';
        return;
      }

      const revealNext = Math.min(remaining, this.getBatchSize());
      button.hidden = false;
      button.classList.remove('is-close');
      button.dataset.action = 'more';
      button.textContent = `SHOW ${revealNext} MORE PRODUCT${revealNext === 1 ? '' : 'S'}`;
    }

    getVariants(card) {
      if (card._peakVariants) return card._peakVariants;
      const data = card.querySelector('[data-product-variants]');
      try {
        card._peakVariants = JSON.parse(data?.textContent || '[]');
      } catch (error) {
        card._peakVariants = [];
      }
      return card._peakVariants;
    }

    getOptionSelects(card) {
      return [...card.querySelectorAll('[data-product-option]')];
    }

    resolveVariant(card, changedSelect = null) {
      const variants = this.getVariants(card);
      if (!variants.length) return null;

      const selects = this.getOptionSelects(card);
      if (!selects.length) return variants[0];

      const values = selects.map(select => select.value);
      let variant = variants.find(item =>
        item.options.length === values.length &&
        item.options.every((value, index) => value === values[index])
      );

      if (variant) return variant;

      const changedIndex = changedSelect ? Number(changedSelect.dataset.optionIndex) : -1;
      const changedValue = changedSelect ? changedSelect.value : null;
      let candidates = variants;

      if (changedIndex >= 0) {
        const matchingChanged = variants.filter(item => item.options[changedIndex] === changedValue);
        if (matchingChanged.length) candidates = matchingChanged;
      }

      variant = candidates
        .map(item => ({
          item,
          score: item.options.reduce((score, value, index) => score + (value === values[index] ? 1 : 0), 0)
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          if (a.item.available !== b.item.available) return a.item.available ? -1 : 1;
          return 0;
        })[0]?.item || variants[0];

      if (variant) {
        selects.forEach((select, index) => {
          if (variant.options[index] != null) select.value = variant.options[index];
        });
      }

      return variant;
    }

    setCardVariant(card, variantId) {
      const variants = this.getVariants(card);
      const variant = variants.find(item => Number(item.id) === Number(variantId));
      if (!variant) return null;

      this.getOptionSelects(card).forEach((select, index) => {
        if (variant.options[index] != null) select.value = variant.options[index];
      });

      return this.refreshCard(card);
    }

    refreshCards() {
      this.root.querySelectorAll('[data-product-card]').forEach(card => this.refreshCard(card));
    }

    refreshCard(card, changedSelect = null) {
      const variant = this.resolveVariant(card, changedSelect);
      if (!variant) return null;

      card.dataset.currentVariantId = String(variant.id);
      card.querySelector('[data-card-price]').textContent = money(variant.price, this.config.moneyFormat);

      const mobilePrice = card.querySelector('[data-card-price-mobile]');
      if (mobilePrice) mobilePrice.textContent = money(variant.price, this.config.moneyFormat);

      const stock = card.querySelector('[data-card-stock]');
      stock.textContent = variant.available ? '' : 'OUT OF STOCK';
      stock.className = `peak-builder__stock ${variant.available ? 'is-in' : 'is-out'}`;

      const mobileStock = card.querySelector('[data-card-stock-mobile]');
      if (mobileStock) {
        mobileStock.textContent = variant.available ? '' : 'OUT OF STOCK';
        mobileStock.className = `peak-builder__stock ${variant.available ? 'is-in' : 'is-out'}`;
      }

      const image = card.querySelector('.peak-builder__product-image img');
      if (image && variant.image) {
        image.removeAttribute('srcset');
        image.src = variant.image;
      }

      return variant;
    }

    getOptionData(card, variant) {
      const selects = this.getOptionSelects(card);
      if (!selects.length || !variant || variant.title === 'Default Title') return [];

      return selects.map((select, index) => ({
        name: select.dataset.optionName || `Option ${index + 1}`,
        value: variant.options[index] ?? select.value
      }));
    }

    select(category, card) {
      const variant = this.refreshCard(card);
      if (!variant) return;

      const img = card.querySelector('.peak-builder__product-image img');
      const title = card.querySelector('h3').textContent.trim();
      const optionData = this.getOptionData(card, variant);

      this.state[category.dataset.categoryId] = {
        category: category.dataset.sourceCategoryLabel || category.querySelector('.peak-builder__category-label').textContent.trim(),
        productId: card.dataset.productId,
        variantId: Number(variant.id),
        title,
        price: Number(variant.price),
        available: Boolean(variant.available),
        image: variant.image || (img ? img.currentSrc || img.src : ''),
        options: optionData,
        quantity: 1
      };

      this.track('peak_builder_product_selected', {
        category_id: category.dataset.categoryId,
        product_id: card.dataset.productId,
        variant_id: variant.id
      });
      this.persist();
      this.render();
      this.closeCategoryAndReturn(category, 'select');
    }

    clear(id) {
      delete this.state[id];
      this.persist();
      this.render();
    }

    changeQty(id, delta) {
      if (!this.state[id]) return;
      this.state[id].quantity = Math.max(1, Math.min(99, this.state[id].quantity + delta));
      this.persist();
      this.render();
    }

    persist() {
      try {
        localStorage.setItem(this.config.storageKey, JSON.stringify({ version: 2, selections: this.state, extraRows: this.extraRows }));
      } catch (error) {}
    }

    restore() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.config.storageKey) || '{}') || {};
        const selections = saved.version === 2 ? saved.selections : saved;
        if (saved.version === 2 && Array.isArray(saved.extraRows)) {
          saved.extraRows.forEach(row => {
            if (row && typeof row.sourceId === 'string' && typeof row.id === 'string' && /^extra-[a-zA-Z0-9-]+$/.test(row.id)) {
              this.createExtraRow(row.sourceId, row.id);
            }
          });
        }
        // Resolve against current merchant products, stock and prices.
        this.categories.forEach(category => {
          const id = category.dataset.categoryId;
          const item = selections && Object.hasOwn(selections, id) ? selections[id] : null;
          if (!item) return;
          const card = [...category.querySelectorAll('[data-product-card]')]
            .find(card => String(card.dataset.productId) === String(item.productId));
          const variant = card && this.getVariants(card).find(variant => Number(variant.id) === Number(item.variantId));
          if (!variant) return;
          this.state[id] = {
            ...item,
            category: category.dataset.sourceCategoryLabel || category.querySelector('.peak-builder__category-label').textContent.trim(),
            title: card.querySelector('h3').textContent.trim(),
            price: Number(variant.price),
            available: Boolean(variant.available),
            options: this.getOptionData(card, variant),
            quantity: Math.max(1, Math.min(99, Math.floor(Number(item.quantity) || 1)))
          };
        });
      } catch (error) {
        this.state = {};
      }
    }

    optionSummary(item) {
      const options = Array.isArray(item.options) ? item.options : [];
      return options.map(option => option.value).filter(Boolean).join(' \u00b7 ');
    }

    renderSelectedControls(category, item) {
      const id = category.dataset.categoryId;
      const extra = this.extraRows.some(row => row.id === id);
      const details = category.querySelector('[data-selected-item]');
      const focused = details.contains(document.activeElement) ? document.activeElement : null;
      const focusedQty = focused && focused.dataset.qty;
      details.hidden = !item && !extra;
      details.innerHTML = item || extra ? `
        ${item ? `<div class="peak-builder__summary-meta"><span>${item.available ? '' : 'OUT OF STOCK'}</span><span>${money(item.price * item.quantity, this.config.moneyFormat)}</span></div>` : ''}
        <div class="peak-builder__summary-controls">
          ${item ? `<div class="peak-builder__qty">
            <button type="button" data-qty="-1" aria-label="Decrease quantity">\u2212</button>
            <span>${item.quantity}</span>
            <button type="button" data-qty="1" aria-label="Increase quantity">+</button>
          </div>` : ''}
          <button type="button" class="peak-builder__remove" data-remove-item aria-label="${extra ? `Remove ${this.escape(category.querySelector('.peak-builder__category-label').textContent)}` : `Remove ${this.escape(item.title)} from build`}">
            REMOVE
          </button>
        </div>` : '';
      details.querySelectorAll('[data-qty]').forEach(button => {
        button.addEventListener('click', () => this.changeQty(id, Number(button.dataset.qty)));
      });
      const remove = details.querySelector('[data-remove-item]');
      if (remove) remove.addEventListener('click', () => {
        this.track('peak_builder_product_removed', { category_id: id });
        if (extra) this.removeExtraRow(id);
        else {
          this.clear(id);
          category.querySelector('[data-open-category]').focus({ preventScroll: true });
        }
      });
      if (focusedQty) details.querySelector(`[data-qty="${focusedQty}"]`)?.focus({ preventScroll: true });
    }

    render() {
      const selected = Object.entries(this.state);

      this.categories.forEach(category => {
        const id = category.dataset.categoryId;
        const item = this.state[id];
        const summary = category.querySelector('[data-selection-summary]');
        const categoryState = category.querySelector('[data-category-state]');
        this.renderSelectedControls(category, item);

        const productCards = [...category.querySelectorAll('[data-product-card]')];
        if (item) {
          const selectedIndex = productCards.findIndex(card => String(item.productId) === String(card.dataset.productId));
          const currentVisible = this.productVisibility.get(id) || this.getBatchSize();
          if (selectedIndex >= currentVisible) {
            this.productVisibility.set(id, selectedIndex + 1);
            this.updateProductVisibility(category);
          }
        }

        productCards.forEach(card => {
          const selectedCard = Boolean(item && String(item.productId) === String(card.dataset.productId));
          card.classList.toggle('is-selected', selectedCard);

          const selectButton = card.querySelector('[data-select-product]');
          selectButton.textContent = selectedCard ? 'SELECTED' : 'SELECT';
          selectButton.setAttribute('aria-pressed', String(selectedCard));

          if (selectedCard && item.variantId) this.setCardVariant(card, item.variantId);
        });

        if (item) {
          const options = this.optionSummary(item);
          summary.innerHTML = `<span class="peak-builder__selection-title">${this.escape(item.title)}</span>${options ? `<span class="peak-builder__selection-options" title="${this.escape(options)}">${this.escape(options)}</span>` : ''}`;
          categoryState.textContent = item.available ? 'SELECTED' : 'SOLD OUT';
        } else {
          summary.textContent = 'Select a product';
          categoryState.textContent = category.dataset.required === 'true' ? 'REQUIRED' : 'OPTIONAL';
        }
      });

      this.updateCompatibilityStates();

      this.categories.forEach(category => this.updateProductVisibility(category));

      const total = selected.reduce((sum, [, item]) => sum + item.price * item.quantity, 0);
      this.root.querySelector('[data-build-total]').textContent = money(total, this.config.moneyFormat);

      const itemCount = selected.reduce((sum, [, item]) => sum + item.quantity, 0);
      const summaryCount = this.root.querySelector('[data-build-item-count]');
      if (summaryCount) summaryCount.textContent = `${itemCount} ITEM${itemCount === 1 ? '' : 'S'}`;

      const mobileReview = this.root.querySelector('[data-mobile-review]');
      if (mobileReview) {
        const count = mobileReview.querySelector('[data-mobile-review-count]');
        const reviewTotal = mobileReview.querySelector('[data-mobile-review-total]');
        if (count) count.textContent = `${itemCount} ITEM${itemCount === 1 ? '' : 'S'}`;
        if (reviewTotal) reviewTotal.textContent = money(total, this.config.moneyFormat);
        this.updateMobileReviewVisibility();
      }

      const missing = this.categories
        .filter(category => category.dataset.required === 'true' && !this.state[category.dataset.categoryId])
        .map(category => category.querySelector('.peak-builder__category-label').textContent.trim());

      const sold = selected.filter(([, item]) => !item.available).map(([, item]) => item.title);
      const compatibilityConflicts = this.getSelectedCompatibilityConflicts();
      const conflictSignature = compatibilityConflicts
        .map(({ a, b }) => [String(a.productId), String(b.productId)].sort().join(':'))
        .sort()
        .join('|');

      if (conflictSignature && conflictSignature !== this._lastConflictSignature) {
        this.track('peak_builder_compatibility_warning', {
          conflict_count: compatibilityConflicts.length
        });
      }
      this._lastConflictSignature = conflictSignature;

      const warnings = this.root.querySelector('[data-build-warnings]');
      const parts = [];

      if (missing.length) {
        parts.push(`<strong>BUILD INCOMPLETE</strong><br>Missing: ${missing.map(value => this.escape(value)).join(', ')}. You can still add available products to cart.`);
      }

      if (compatibilityConflicts.length) {
        const conflictText = compatibilityConflicts
          .map(({ a, b }) => `${this.escape(a.title)} + ${this.escape(b.title)}`)
          .join('<br>');
        parts.push(`<strong>COMPATIBILITY WARNING</strong><br>${conflictText}`);
      }

      if (sold.length) {
        parts.push(`<strong>OUT OF STOCK</strong><br>${sold.map(value => this.escape(value)).join(', ')} will remain in this saved build but will not be added to cart.`);
      }

      warnings.innerHTML = parts.join('<br><br>');
      warnings.hidden = !parts.length;

      const purchasable = selected.filter(([, item]) => item.available);
      this.root.querySelector('[data-add-build]').disabled = purchasable.length === 0;
    }

    async getCartState() {
      const response = await fetch(this.config.cartJsonUrl || `${this.config.cartUrl}.js`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(
          data.description ||
          data.message ||
          'Unable to verify cart.'
        );
      }

      return data;
    }

    cartVariantQuantities(cart) {
      const quantities = new Map();

      (cart.items || []).forEach(item => {
        const variantId = Number(item.variant_id || item.id);
        quantities.set(
          variantId,
          (quantities.get(variantId) || 0) + Number(item.quantity || 0)
        );
      });

      return quantities;
    }

    verifyCartIncrement(beforeCart, afterCart, items) {
      const before = this.cartVariantQuantities(beforeCart);
      const after = this.cartVariantQuantities(afterCart);
      const expected = new Map();

      items.forEach(item => {
        const variantId = Number(item.id);
        expected.set(
          variantId,
          (expected.get(variantId) || 0) + Number(item.quantity || 0)
        );
      });

      for (const [variantId, increment] of expected.entries()) {
        const beforeQty = before.get(variantId) || 0;
        const afterQty = after.get(variantId) || 0;

        if (afterQty < beforeQty + increment) return false;
      }

      return true;
    }

    wait(ms) {
      return new Promise(resolve => window.setTimeout(resolve, ms));
    }

    async addToCart() {
      const button = this.root.querySelector('[data-add-build]');
      const status = this.root.querySelector('[data-builder-status]');

      const items = Object.values(this.state)
        .filter(item => item.available)
        .map(item => {
          const properties = {
            '_Peak Build': 'Sim Builder',
            '_Component': item.category
          };

          const configuration = this.optionSummary(item);
          if (configuration) properties['_Configuration'] = configuration;

          return {
            id: Number(item.variantId),
            quantity: Number(item.quantity),
            properties
          };
        });

      if (!items.length) return;

      button.disabled = true;
      status.textContent = 'ADDING BUILD\u2026';

      try {
        const beforeCart = await this.getCartState();

        /*
          Mutation is performed exactly once. Never retry this POST
          automatically because a successful retry would duplicate the build.
        */
        const response = await fetch(this.config.cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items })
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(
            data.description ||
            data.message ||
            'Unable to add build.'
          );
        }

        /*
          Verify the actual same-origin server cart before opening the cart.
          If a read is momentarily stale, retry GET only - never the POST.
        */
        let afterCart = await this.getCartState();
        let verified = this.verifyCartIncrement(beforeCart, afterCart, items);

        if (!verified) {
          await this.wait(250);
          afterCart = await this.getCartState();
          verified = this.verifyCartIncrement(beforeCart, afterCart, items);
        }

        if (!verified) {
          await this.wait(500);
          afterCart = await this.getCartState();
          verified = this.verifyCartIncrement(beforeCart, afterCart, items);
        }

        console.info('[Peak Sim Builder] Cart verification', {
          origin: window.location.origin,
          beforeItemCount: beforeCart.item_count,
          afterItemCount: afterCart.item_count,
          expectedVariantIds: items.map(item => item.id),
          verified
        });

        if (!verified) {
          throw new Error(
            'Cart could not be verified. Your build is still saved. Please try again.'
          );
        }

        const skipped = Object.values(this.state).filter(item => !item.available);

        status.textContent =
          `${items.length} PRODUCT${items.length === 1 ? '' : 'S'} ADDED TO CART` +
          `${skipped.length ? ` \u00b7 ${skipped.length} OUT-OF-STOCK SKIPPED` : ''}`;

        this.track('peak_builder_added_to_cart', {
          added_count: items.length,
          skipped_count: skipped.length
        });

        if (!window.openPeakCart?.()) window.location.assign(this.config.cartUrl);
      } catch (error) {
        console.error('[Peak Sim Builder] Add to cart failed:', {
          origin: window.location.origin,
          message: error && error.message ? error.message : String(error)
        });

        status.textContent =
          error && error.message
            ? error.message
            : 'Unable to add build. Please try again.';
      } finally {
        button.disabled = false;
      }
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      })[character]);
    }
  }

  const init = scope => scope.querySelectorAll('[data-peak-builder]').forEach(element => {
    if (!element.dataset.initialized) {
      element.dataset.initialized = 'true';
      new PeakSimBuilder(element);
    }
  });

  document.addEventListener('DOMContentLoaded', () => init(document));
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
